// ─── Atelier chassis ─────────────────────────────────────────────────────────
// Generated from the blueprint, in the same pass as the Go structs. These two
// cannot drift: change the blueprint, not this file.

export type ListResponse<T> = {
  items: T[];
  total: number;
  limit: number;
  offset: number;
};

export type ApiError = {
  error: { code: string; message: string; fields?: Record<string, string> };
};

export type FeedbackCategory =
  | "room"
  | "cleanliness"
  | "staff"
  | "dining"
  | "amenities"
  | "booking"
  | "other";

export const FeedbackCategoryValues: FeedbackCategory[] = ["room", "cleanliness", "staff", "dining", "amenities", "booking", "other"];

export type FollowUpStatus =
  | "new"
  | "in_progress"
  | "responded"
  | "closed";

export const FollowUpStatusValues: FollowUpStatus[] = ["new", "in_progress", "responded", "closed"];

export type GuestSentiment =
  | "positive"
  | "neutral"
  | "negative";

export const GuestSentimentValues: GuestSentiment[] = ["positive", "neutral", "negative"];

export type User = {
  id: string;
  email: string;
  name: string;
  role: "viewer" | "member" | "admin" | "owner";
  last_active_at?: string | null;
  created_at: string;
  updated_at: string;
};

/** A single piece of guest feedback with a rating, category, comments, and follow-up status. */
export type Feedback = {
  id: string;
  guest_name: string;
  comments: string;
  rating: number;
  category: FeedbackCategory;
  sentiment: GuestSentiment;
  status: FollowUpStatus;
  room_number?: string | null;
  guest_email?: string | null;
  stay_date?: string | null;
  response_notes?: string | null;
  responded_at?: string | null;
  staff_id?: string;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
};

/** The writable shape: the server assigns id, owner and timestamps. */
export type FeedbackInput = Omit<Feedback, "id" | "created_at" | "updated_at" | "deleted_at">;

/** Column metadata, so tables and forms are described once rather than per page. */
export const FeedbackFields = [
  { name: "guest_name" as const, label: "Guest Name", type: "string", required: true },
  { name: "comments" as const, label: "Comments", type: "text", required: true },
  { name: "rating" as const, label: "Rating", type: "int", required: true },
  { name: "category" as const, label: "Category", type: "enum", required: true, enum: FeedbackCategoryValues },
  { name: "sentiment" as const, label: "Sentiment", type: "enum", required: true, enum: GuestSentimentValues },
  { name: "status" as const, label: "Status", type: "enum", required: true, enum: FollowUpStatusValues },
  { name: "room_number" as const, label: "Room Number", type: "string", required: false },
  { name: "guest_email" as const, label: "Guest Email", type: "email", required: false },
  { name: "stay_date" as const, label: "Stay Date", type: "date", required: false },
  { name: "response_notes" as const, label: "Response Notes", type: "text", required: false },
  { name: "responded_at" as const, label: "Responded At", type: "time", required: false },
];

/** A hotel staff member who can own and follow up on feedback. */
export type Staff = {
  id: string;
  name: string;
  email: string;
  role_title?: string | null;
  phone?: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
};

/** The writable shape: the server assigns id, owner and timestamps. */
export type StaffInput = Omit<Staff, "id" | "created_at" | "updated_at">;

/** Column metadata, so tables and forms are described once rather than per page. */
export const StaffFields = [
  { name: "name" as const, label: "Name", type: "string", required: true },
  { name: "email" as const, label: "Email", type: "email", required: true },
  { name: "role_title" as const, label: "Role Title", type: "string", required: false },
  { name: "phone" as const, label: "Phone", type: "phone", required: false },
  { name: "active" as const, label: "Active", type: "bool", required: true },
];

/** A logged response sent to a guest for a piece of feedback. */
export type Reply = {
  id: string;
  message: string;
  channel?: string | null;
  sent_at: string;
  feedback_id?: string;
  staff_id?: string;
  created_at: string;
  updated_at: string;
};

/** The writable shape: the server assigns id, owner and timestamps. */
export type ReplyInput = Omit<Reply, "id" | "created_at" | "updated_at">;

/** Column metadata, so tables and forms are described once rather than per page. */
export const ReplyFields = [
  { name: "message" as const, label: "Message", type: "text", required: true },
  { name: "channel" as const, label: "Channel", type: "string", required: false },
  { name: "sent_at" as const, label: "Sent At", type: "time", required: true },
];
